/**
 * BOSS Genel Bakış.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Users, Building2, ArrowDownToLine, ArrowUpFromLine,
  Activity, Clock, RefreshCw, Cake, AlertCircle, ChevronRight,
} from 'lucide-react'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import {
  bugunkuTumKayitlar,
  sonKayitHaritasi,
  type Kayit,
} from '@/lib/services/kayit-service'
import {
  yaklasanDogumGunleri, yilKatsayilariGetir,
  type DogumGunuKatsayisi,
} from '@/lib/services/dogum-service'
import { BekleyenDevamsizKart } from '@/components/devamsiz/BekleyenDevamsizKart'
import { BekleyenExtraKart } from '@/components/extra/BekleyenExtraKart'
import { BekleyenMaasHareketiKart } from '@/components/maas/BekleyenMaasHareketiKart'
import { GununOzetiKarti } from '@/components/ozet/GununOzetiKarti'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel, Sube } from '@/types'

interface YaklasanDg {
  personel: KullaniciPersonel
  dogumGunuIso: string
  kalanGun: number
  katsayi: DogumGunuKatsayisi | null
}

export function BossOverview() {
  const { kullanici } = useAuth()
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [bugunkuKayitlar, setBugunkuKayitlar] = useState<Kayit[]>([])
  const [yaklasanDgListesi, setYaklasanDgListesi] = useState<YaklasanDg[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [yenileniyor, setYenileniyor] = useState(false)
  const [sonGuncelleme, setSonGuncelleme] = useState<Date>(new Date())
  const [hata, setHata] = useState<string | null>(null)

  const [su_an, setSuAn] = useState<Date>(new Date())
  useEffect(() => {
    const t = setInterval(() => setSuAn(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setYukleniyor(true)
    setYenileniyor(true)
    try {
      const buYil = new Date().getFullYear()
      const [p, s, k, katsayilar] = await Promise.all([
        tumPersonelleriGetir(),
        tumSubeleriGetir(),
        bugunkuTumKayitlar(),
        yilKatsayilariGetir(buYil),
      ])
      const aktifPersoneller = p.filter(x => x.aktif)
      setPersoneller(aktifPersoneller)
      setSubeler(s.filter(x => x.aktif))
      setBugunkuKayitlar(k)

      const yaklasan = yaklasanDogumGunleri(aktifPersoneller, 7)
      const katsayiMap = new Map<string, DogumGunuKatsayisi>()
      katsayilar.forEach(kk => katsayiMap.set(kk.personelId, kk))
      setYaklasanDgListesi(
        yaklasan.map(y => ({
          personel: y.personel,
          dogumGunuIso: y.dogumGunuIso,
          kalanGun: y.kalanGun,
          katsayi: katsayiMap.get(y.personel.id) ?? null,
        }))
      )

      setSonGuncelleme(new Date())
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Veriler yüklenemedi.')
    } finally {
      setYukleniyor(false)
      setYenileniyor(false)
    }
  }, [])

  useEffect(() => { yukle() }, [yukle])

  useEffect(() => {
    const i = setInterval(() => yukle(true), 30000)
    return () => clearInterval(i)
  }, [yukle])

  const sonKayitlar = useMemo(() => sonKayitHaritasi(bugunkuKayitlar), [bugunkuKayitlar])

  const aktifPersoneller = useMemo(() => {
    return personeller.filter(p => sonKayitlar.get(p.id)?.tip === 'giris')
  }, [personeller, sonKayitlar])

  const subeyeGoreAktif = useMemo(() => {
    const harita = new Map<string, KullaniciPersonel[]>()
    subeler.forEach(s => harita.set(s.id, []))
    aktifPersoneller.forEach(p => {
      const son = sonKayitlar.get(p.id)
      if (son) {
        const liste = harita.get(son.subeId) ?? []
        liste.push(p)
        harita.set(son.subeId, liste)
      }
    })
    return harita
  }, [aktifPersoneller, sonKayitlar, subeler])

  const bugunGirisSayisi = bugunkuKayitlar.filter(k => k.tip === 'giris').length
  const bugunCikisSayisi = bugunkuKayitlar.filter(k => k.tip === 'cikis').length

  const personelHaritasi = useMemo(() => {
    const m = new Map<string, KullaniciPersonel>()
    personeller.forEach(p => m.set(p.id, p))
    return m
  }, [personeller])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach(s => m.set(s.id, s))
    return m
  }, [subeler])

  const sonHareketler = bugunkuKayitlar.slice(0, 8)
  const kararsizDg = yaklasanDgListesi.filter(y => !y.katsayi)

  const tarihStr = su_an.toLocaleDateString('tr-TR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
  const saatStr = su_an.toLocaleTimeString('tr-TR', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })

  if (yukleniyor) {
    return (
      <div className="card text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
        <p className="text-gray-600">Yükleniyor...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Genel Bakış</h1>
          <p className="text-sm text-gray-600 mt-1">
            Son güncelleme: {sonGuncelleme.toLocaleTimeString('tr-TR')}
            {' · '}
            <button
              onClick={() => yukle()}
              disabled={yenileniyor}
              className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1"
            >
              <RefreshCw className={`w-3 h-3 ${yenileniyor ? 'animate-spin' : ''}`} />
              Yenile
            </button>
          </p>
        </div>
        <div className="bg-gradient-to-br from-brand-500 to-indigo-600 text-white rounded-2xl px-6 py-3 shadow-md min-w-[220px] flex flex-col items-center justify-center text-center">
          <p className="text-3xl font-bold tabular-nums leading-none">{saatStr}</p>
          <p className="text-xs opacity-90 capitalize whitespace-nowrap mt-1.5">{tarihStr}</p>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
          {hata}
        </div>
      )}

      {/* Karar bekleyen devamsızlıklar — feed kartı (en üstte, görünür) */}
      <BekleyenDevamsizKart
        personeller={personeller}
        subeler={subeler}
        onYenile={() => yukle()}
      />

      {/* Extra mesai onayı bekleyenler */}
      {kullanici && (
        <BekleyenExtraKart
          onaylayanId={kullanici.id}
          subeler={subeler}
          subeFiltre={null}
          onYenile={() => yukle()}
        />
      )}

      {/* Maaş hareketi (kasa eksiği vb.) onayı bekleyenler */}
      {kullanici && (
        <BekleyenMaasHareketiKart
          onaylayanId={kullanici.id}
          onYenile={() => yukle()}
        />
      )}

      <GununOzetiKarti />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetrikKart ikon={Activity} renk="green" baslik="Şu An Aktif" deger={aktifPersoneller.length} altMetin={`/ ${personeller.length} kişi`} />
        <MetrikKart ikon={Building2} renk="indigo" baslik="Aktif Şube" deger={subeler.length} />
        <MetrikKart ikon={ArrowDownToLine} renk="blue" baslik="Bugün Giriş" deger={bugunGirisSayisi} />
        <MetrikKart ikon={ArrowUpFromLine} renk="orange" baslik="Bugün Çıkış" deger={bugunCikisSayisi} />
      </div>

      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Şube Durumu</h2>
        {subeler.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">Henüz şube tanımlanmamış.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {subeler.map(sube => {
              const aktifler = subeyeGoreAktif.get(sube.id) ?? []
              return (
                <div key={sube.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 truncate">{sube.isim}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{sube.adres}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                        aktifler.length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          aktifler.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                        }`} />
                        {aktifler.length} aktif
                      </div>
                    </div>
                  </div>

                  {aktifler.length > 0 ? (
                    <div className="space-y-1.5">
                      {aktifler.map(p => {
                        const son = sonKayitlar.get(p.id)
                        const dakikaIceride = son ? Math.floor((Date.now() - son.zaman.toMillis()) / 60000) : 0
                        const saat = Math.floor(dakikaIceride / 60)
                        const dk = dakikaIceride % 60
                        return (
                          <div key={p.id} className="flex items-center justify-between text-sm bg-green-50 rounded-lg px-3 py-2">
                            <span className="font-medium text-gray-900">{p.isim}</span>
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {saat > 0 && `${saat}s `}{dk}dk
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Şu an aktif personel yok</p>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Bugünkü Son Hareketler</h2>
        {sonHareketler.length === 0 ? (
          <div className="card text-center py-8 text-gray-500">Bugün henüz hareket yok.</div>
        ) : (
          <div className="card divide-y divide-gray-100">
            {sonHareketler.map(k => {
              const personel = personelHaritasi.get(k.personelId)
              const sube = subeHaritasi.get(k.subeId)
              return (
                <div key={k.id} className="py-2.5 first:pt-0 last:pb-0 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    k.tip === 'giris' ? 'bg-green-100' : 'bg-orange-100'
                  }`}>
                    {k.tip === 'giris' ? (
                      <ArrowDownToLine className="w-4 h-4 text-green-700" />
                    ) : (
                      <ArrowUpFromLine className="w-4 h-4 text-orange-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {personel?.isim ?? 'Bilinmeyen'}
                      <span className="text-gray-500 font-normal ml-2">
                        {k.tip === 'giris' ? 'giriş yaptı' : 'çıkış yaptı'}
                      </span>
                      {k.yontem !== 'qr' && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                          manuel
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {sube?.isim ?? '?'}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 flex-shrink-0">
                    {k.zaman.toDate().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {yaklasanDgListesi.length > 0 && (
        <div className={`card border-2 ${
          kararsizDg.length > 0
            ? 'bg-gradient-to-br from-pink-50 to-amber-50 border-pink-300'
            : 'bg-pink-50 border-pink-200'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Cake className="w-5 h-5 text-pink-600" />
              Yaklaşan Doğum Günleri (7 gün)
            </h2>
            <Link to="/boss/dogumgunleri" className="text-pink-700 hover:text-pink-900 text-sm font-semibold inline-flex items-center gap-1">
              Tümü <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {kararsizDg.length > 0 && (
            <div className="bg-amber-100 border border-amber-300 rounded-lg p-2.5 mb-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>{kararsizDg.length} personel</strong> için katsayı belirlemedin.
                Personel giriş yapmadan önce karar ver.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            {yaklasanDgListesi.map(y => (
              <DgSatiri key={y.personel.id} veri={y} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DgSatiri({ veri }: { veri: YaklasanDg }) {
  const { personel, dogumGunuIso, kalanGun, katsayi } = veri
  const bugun = kalanGun === 0
  const yarin = kalanGun === 1
  const d = new Date(dogumGunuIso + 'T00:00:00')
  const tarihStr = d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })
  return (
    <Link to="/boss/dogumgunleri" className="flex items-center justify-between gap-2 bg-white rounded-lg p-2.5 hover:bg-pink-50 transition-colors border border-pink-100">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-lg">{bugun ? '🎂' : '🎈'}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 truncate text-sm">{personel.isim}</p>
          <p className="text-xs text-gray-600">
            {tarihStr}
            {bugun && <span className="ml-1 text-pink-700 font-bold">· BUGÜN!</span>}
            {yarin && <span className="ml-1 text-pink-700 font-semibold">· Yarın</span>}
            {kalanGun > 1 && <span className="ml-1 text-gray-500">· {kalanGun} gün</span>}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0">
        {katsayi ? (
          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-bold">x{katsayi.katsayi}</span>
        ) : (
          <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-semibold inline-flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Karar bekliyor
          </span>
        )}
      </div>
    </Link>
  )
}

interface MetrikKartProps {
  ikon: typeof Users
  renk: 'green' | 'indigo' | 'blue' | 'orange'
  baslik: string
  deger: string | number
  altMetin?: string
}

const RENK_KLASSLARI: Record<string, { bg: string; text: string }> = {
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700' },
}

function MetrikKart({ ikon: Icon, renk, baslik, deger, altMetin }: MetrikKartProps) {
  const k = RENK_KLASSLARI[renk] ?? RENK_KLASSLARI.gray
  return (
    <div className="card p-3 md:p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${k.bg}`}>
          <Icon className={`w-4 h-4 ${k.text}`} />
        </div>
        <p className="text-xs text-gray-500 font-medium">{baslik}</p>
      </div>
      <div className="flex items-baseline gap-1">
        <p className="text-2xl md:text-3xl font-bold text-gray-900">{deger}</p>
        {altMetin && <p className="text-xs text-gray-500">{altMetin}</p>}
      </div>
    </div>
  )
}
